import { z } from 'zod'
import { MIRA_PADRAO_M, MiraM } from './mira'
import { ROOM_PASSWORD_MAX } from './senha'
import { TETO_DIGITOS_VALOR } from './valores'

/**
 * Eventos de mesa: melhor de 3 e torneio, amistosos nesta fase.
 *
 * "Evento" aqui é o formato da mesa, e não o `evento()` dos códigos de erro
 * traduzidos (`eventos.ts`) — por isso os nomes levam "de mesa".
 *
 * Regras e desenho em `docs/superpowers/specs/2026-09-25-formatos-de-mesa-design.md`.
 */

export const FormatoDeMesa = z.enum(['melhor-de-3', 'torneio'])
export type FormatoDeMesa = z.infer<typeof FormatoDeMesa>

export const PartidaDoEventoDeMesa = z.object({
  matchId: z.string(),
  etapa: z.enum(['jogo', 'semifinal', 'final']),
  /** 1, 2, 3 na melhor de 3; 1 e 2 nas semifinais; 1 na final. */
  numero: z.number().int(),
  /** Na ordem dos índices da partida. */
  jogadores: z.tuple([z.string(), z.string()]),
  /** Começa sem a disputa da saída, com o jogador 0 quebrando. */
  semDisputa: z.boolean(),
  estado: z.enum(['agendada', 'jogando', 'terminada', 'anulada']),
  /** Quando a partida agendada abre, em ms do servidor. */
  abreEm: z.number(),
  vencedor: z.string().nullable(),
  quebrou: z.string().nullable(),
})
export type PartidaDoEventoDeMesa = z.infer<typeof PartidaDoEventoDeMesa>

/**
 * Por que o evento acabou sem campeão (ou se desfez), como CÓDIGO: quem
 * traduz é a tela (`eventoMesa.motivo.<código>`), como os demais eventos.
 * Antes o servidor mandava a frase pronta em português, que aparecia assim
 * também com o jogo em inglês (#48, T6).
 */
export const MOTIVOS_DO_EVENTO = [
  'todos_sairam',
  'partida_travou',
  'final_travou',
  'semifinal_travou',
  'nao_fechou',
  'passou_do_tempo',
  /** O dono cancelou e mandou devolver as inscricoes. */
  'cancelado_pelo_dono',
] as const
export type MotivoDoEvento = (typeof MOTIVOS_DO_EVENTO)[number]

export const EventoDeMesa = z.object({
  id: z.string(),
  formato: FormatoDeMesa,
  mode: z.enum(['eightball', 'sinuca']),
  label: z.string(),
  criador: z.string(),
  /** Na ordem em que entraram. */
  jogadores: z.array(z.string()).max(4),
  /** Apelidos, na mesma ordem de `jogadores`. Nulo onde não há. */
  nomes: z.array(z.string().nullable()).default([]),
  estado: z.enum(['aberto', 'jogando', 'terminado', 'desfeito']),
  criadoEm: z.number(),
  /** Até quando o evento espera fechar, em ms do servidor. */
  fechaEm: z.number(),
  partidas: z.array(PartidaDoEventoDeMesa),
  campeao: z.string().nullable(),
  /** Os dois finalistas quando a final travou. */
  divididoEntre: z.tuple([z.string(), z.string()]).nullable(),
  /**
   * Um `MotivoDoEvento`. Texto livre no schema, e não `z.enum`: um motivo que
   * a tela não conhece (servidor mais novo, ou o texto de um mais antigo) não
   * pode derrubar a mensagem inteira — a tela mostra o que veio.
   */
  motivo: z.string().nullable(),
  /**
   * Quanto da linha-guia as partidas deste evento mostram.
   *
   * Decidido por quem CRIA, como na mesa 1x1, e imutável depois. Sem este
   * campo a melhor de 3 e o torneio jogavam SEMPRE na mira normal: a tela lê
   * o alcance da SALA, e partida de evento não tem sala. Ninguém tinha
   * escolhido aquela mira — e é a escolha que torna a mesa honesta, porque
   * quem entra vê o valor na listagem ANTES de entrar.
   *
   * Mesmo default da sala, pelo mesmo motivo: evento criado antes deste campo
   * continua sendo o que sempre foi.
   */
  miraM: MiraM.default(MIRA_PADRAO_M),
  /**
   * O evento tem senha: só entra quem a souber. Derivado do hash no servidor
   * — a senha, em claro ou em hash, nunca viaja. Ausente = aberto, como os
   * eventos de antes deste campo.
   */
  protegido: z.boolean().default(false),
  /**
   * O evento tem voz: ligada por quem CRIA, como na mesa 1x1, e desligada por
   * padrão. Na melhor de 3 os dois conversam a série inteira; no torneio, os
   * dois de cada partida (quem assiste ouve, não fala). Ausente = sem voz,
   * como os eventos de antes deste campo.
   */
  voice: z.boolean().default(false),
  /**
   * Plateia aberta: qualquer um assiste. Fechada, só quem é do evento — o que
   * inclui quem foi eliminado no torneio, que continua em `jogadores`.
   * Escolhida por quem CRIA, como na mesa 1x1. Ausente = aberta, como eram
   * todos os eventos antes deste campo.
   */
  spectators: z.boolean().default(true),
  /**
   * A ENTRADA do torneio, em lamports, como string de dígitos.
   *
   * Lamports não cabem com segurança num número de JSON — o mesmo motivo
   * pelo qual o `stake` de `lobby.reserve` é string. Cobrada **uma vez**, no
   * registro no torneio, e não por partida.
   *
   * `'0'` é AMISTOSO, e é o que todo evento é hoje: evento gravado antes do
   * campo volta amistoso, e um cliente de antes dele não passa a criar
   * torneio cobrado sem saber.
   */
  stake: z.string().max(TETO_DIGITOS_VALOR).regex(/^\d+$/).default('0'),
  /**
   * A assinatura da transacao que FECHOU o torneio na chain.
   *
   * Liquidado: o pagamento ao campeao. Cancelado: a transacao que abriu o
   * saque de cada um. Nulo: nao houve (amistoso), ou ainda nao aconteceu.
   *
   * Existe porque a tela nao tinha o que mostrar. O campeao lia "e o
   * campeao!", recebia 0,185 SOL de verdade, e NADA na tela dizia que o
   * pagamento aconteceu -- o servidor escrevia a assinatura no log e a jogava
   * fora. Pagamento sem comprovante a vista e indistinguivel de pagamento que
   * nao aconteceu, e foi assim que o bug real (`NotReferee`) passou dias sem
   * ser notado.
   */
  liquidacao: z.string().max(100).nullable().default(null),
  /**
   * Quanto o campeao recebeu DE FATO, em lamports, como string.
   *
   * Medido na transacao confirmada (saldo depois menos saldo antes), nao
   * calculado pelos cortes: o numero que a tela mostra como comprovante tem de
   * ser o que a chain pagou, senao e so a nossa conta repetida de volta.
   */
  premio: z.string().max(TETO_DIGITOS_VALOR).regex(/^\d+$/).default('0'),
  /**
   * O acumulado das entradas, para o lobby dizer quanto está em jogo sem
   * consultar a chain a cada redesenho da lista.
   *
   * É valor derivado (entrada × inscritos), e viaja pronto de propósito: a
   * tela não deve refazer conta de dinheiro.
   */
  pote: z.string().max(TETO_DIGITOS_VALOR).regex(/^\d+$/).default('0'),
})
export type EventoDeMesa = z.infer<typeof EventoDeMesa>

const TETO_ID = 64

/**
 * Teto do que o PROTOCOLO aceita como senha, bem acima do que vale: a faixa
 * de verdade (`ROOM_PASSWORD_MIN..MAX`) é conferida no servidor, que recusa com
 * o motivo. Mesmo teto de `lobby.reserve`.
 */
const TETO_SENHA_DO_EVENTO = ROOM_PASSWORD_MAX * 5

/** Mensagens do cliente sobre eventos de mesa. */
export const MensagensDoClienteDeEvento = [
  z.object({
    t: z.literal('evento.criar'),
    formato: FormatoDeMesa,
    mode: z.enum(['eightball', 'sinuca']),
    label: z.string().max(80).optional(),
    /** Opcional como em `lobby.reserve`: sem ele, a mira de hoje. */
    miraM: MiraM.optional(),
    /** Voz entre quem joga. Sem ele, desligada — como na mesa 1x1. */
    voice: z.boolean().optional(),
    /**
     * Plateia aberta. Sem ele, ABERTA: é o que o evento sempre foi, e um
     * cliente de antes do campo não pode passar a criar eventos fechados sem
     * saber. A tela de hoje manda sempre, desligado por padrão como na 1x1.
     */
    spectators: z.boolean().optional(),
    /**
     * A entrada do torneio, em lamports. Sem ela, o torneio é amistoso.
     *
     * Escolhida ao CRIAR a sala, antes de existir inscrito: quem se registra
     * vê o valor antes de depositar, e o valor não muda debaixo de quem já
     * pagou.
     */
    stake: z.string().max(TETO_DIGITOS_VALOR).regex(/^\d+$/).optional(),
    /**
     * O id do torneio, escolhido pela TELA, so no torneio pago.
     *
     * A tela precisa assinar `create_tournament` antes de o servidor abrir a
     * sala, e a PDA do torneio vem do id -- com o servidor gerando o id, nao
     * haveria o que assinar. O servidor exige hex de 32, recusa id em uso e
     * so abre a sala se a conta EXISTIR na chain, entao um id inventado sem a
     * conta nao vira torneio nenhum.
     *
     * Ausente: o servidor gera o id, como sempre fez no amistoso.
     */
    id: z.string().regex(/^[0-9a-f]{32}$/).optional(),
    /** Senha para entrar, como a da mesa 1x1. Sem ela, qualquer um entra. */
    password: z.string().max(TETO_SENHA_DO_EVENTO).optional(),
  }),
  z.object({
    t: z.literal('evento.entrar'),
    id: z.string().max(TETO_ID),
    /** A senha do evento, quando ele tem uma. Quem já está nele não precisa. */
    password: z.string().max(TETO_SENHA_DO_EVENTO).optional(),
  }),
  /** Sair antes de o evento fechar. Depois de começar, sair é perder pelo relógio. */
  z.object({ t: z.literal('evento.sair') }),
  /**
   * O DONO pede para cancelar o torneio pago e devolver as inscricoes.
   *
   * Pedir ao servidor, e nao assinar: `cancel_tournament` so aceita o
   * `config.referee` ou qualquer um depois do prazo, e o criador nao e
   * nenhum dos dois. O servidor confere que quem pede criou o torneio e
   * aciona como referee -- o destino de cada lamport continua fixado pelo
   * contrato, entao acionar nao e escolher.
   */
  z.object({ t: z.literal('torneio.cancelar'), id: z.string().max(TETO_ID) }),
  /**
   * Assistir o evento: a partida em curso dele (ou a que está para abrir).
   * `matchId` escolhe uma partida do evento quando há mais de uma (as duas
   * semifinais); sem ele, a primeira em jogo.
   */
  z.object({ t: z.literal('evento.assistir'), id: z.string().max(TETO_ID), matchId: z.string().max(TETO_ID).optional() }),
] as const

/** Mensagens do servidor sobre eventos de mesa. */
export const MensagensDoServidorDeEvento = [
  /** A lista inteira, ao se inscrever no lobby. */
  z.object({ t: z.literal('evento.lista'), eventos: z.array(EventoDeMesa) }),
  /** Um evento mudou (inclusive terminou ou se desfez, uma última vez). */
  z.object({ t: z.literal('evento.atualizado'), evento: EventoDeMesa }),
] as const
